import Tool, { BaseTool, type ToolButtonComponent } from './Tool';
import React, { useRef } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarRow from '../toolbars/ToolbarRow';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Instance as TippyInstance } from 'tippy.js/index';
import { tools } from '@aha-app/aha-editor-v2';

type ToolTextListOption = typeof BaseTool & {
  listType: string;
};

const ListButton = (
  tool: ToolTextListOption,
  icon: string
): React.FC<{
  currentList?: string;
  setList: (tool: ToolTextListOption) => void;
  hidePopup: () => void;
}> => {
  return ApplicationView(({ currentList, setList, hidePopup }) => (
    <ToolButton
      active={currentList === tool.listType}
      onClick={() => {
        setList(tool);
        hidePopup();
      }}
      tool={tool}
    >
      <aha-icon icon={icon} />
    </ToolButton>
  ));
};

class ToolTextListBullet extends BaseTool {
  static toolName = 'Bullet';
  static listType = 'ul_list';

  static buttonComponent() {
    return ListButton(ToolTextListBullet, 'fa-regular fa-list-ul');
  }

  static action({ controller, selection }) {
    selection.applyTextProperty(editor => {
      tools.ToolBulletedList.action({ editor });
    });
    controller.canvas.requestRenderAll();
  }
}

class ToolTextListNumber extends BaseTool {
  static toolName = 'Number';
  static listType = 'ol_list';

  static buttonComponent() {
    return ListButton(ToolTextListNumber, 'fa-regular fa-list-ol');
  }

  static action({ controller, selection }) {
    selection.applyTextProperty(editor => {
      tools.ToolNumberedList.action({ editor });
    });
    controller.canvas.requestRenderAll();
  }
}

const Popup: React.FC<{ selection?: fabric.Object; hidePopup: () => void }> =
  ApplicationView(({ hidePopup }) => {
    const controller = useController(WhiteboardController);
    const listButtons = [
      ToolTextListBullet.buttonComponent(),
      ToolTextListNumber.buttonComponent(),
    ];

    const setList = tool => {
      controller.actionApplyTool(tool);
    };

    return (
      <ToolbarMenu columns={2}>
        <ToolbarRow>
          {listButtons.map((ListButton, index) => (
            <ListButton key={index} setList={setList} hidePopup={hidePopup} />
          ))}
        </ToolbarRow>
      </ToolbarMenu>
    );
  });

const TextListButton: ToolButtonComponent = ApplicationView(
  ({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();

    return (
      <ToolButton
        popup={
          <Popup
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        <aha-icon icon='fa-regular fa-list-ul' />
      </ToolButton>
    );
  }
);

class ToolTextList extends Tool {
  static toolName = 'List';

  static buttonComponent() {
    return TextListButton;
  }
}

export { ToolTextList, ToolTextListBullet, ToolTextListNumber };
